/**
 * Deterministic, offline AMA (11th edition) reference-list linter.
 *
 * The same contract as the other reference linters (`src/lib/citation-check*.ts`):
 * pure string in, findings out, high precision, every finding a likely issue to review
 * rather than a verdict. It does not import the citation core. The words of each
 * finding live in the widget's strings, keyed by `rule`.
 *
 * AMA and Vancouver share a skeleton (`medical-parts.ts`) and differ in the details
 * this file tests. Rules, from the AMA Manual of Style, 11th ed. (2020), as quoted by
 * university library guides because the manual itself is behind a subscription:
 * - Authors: "Use the author's surname followed by initials without periods or
 *   spaces. … the names of all authors should be given unless there are more than 6,
 *   in which case the names of the first 3 authors are used, followed by 'et al.' Do
 *   not use 'and' between names." (West Virginia University Libraries, AMA 11.)
 * - Article titles in sentence case; journal names abbreviated as in the NLM Catalog
 *   and italicized (italics cannot be seen in pasted plain text, so they are not
 *   checked). (University of New England Library, AMA 11.)
 * - "The year, followed by a semicolon; the volume number and the issue number (in
 *   parentheses), followed by a colon; the initial page number, a hyphen, and the final
 *   page number, followed by a period, are set without spaces. Do not omit digits from
 *   inclusive page numbers. … The DOI should be the final element and is not followed
 *   by a period." The manual prefers "doi:10.1001/jama.2017.13737" to a doi.org URL.
 *   (New York Medical College Library, AMA 11 digital resources.)
 * - "The DOI is provided immediately after 'doi:' with no spaces." (WVU Libraries.)
 * - References are numbered consecutively in the order they are first cited.
 */

import {
  type AuthorList,
  correctedAuthors,
  findLocator,
  journalSegment,
  type Locator,
  nameFormIssue,
  parseAuthors,
  RE_IEEE_LOCATORS,
  readTitle,
  type TitlePart,
  undottedJournal,
  usesConjunction,
} from "@/lib/tool-kit/reference-checkers/medical-parts";
import {
  alphabeticalFinding,
  type CheckReport,
  duplicateFindings,
  type EntryReport,
  endsWithLink,
  endsWithTerminal,
  type Finding,
  finishReport,
  fullRange,
  type ListEntry,
  type ListRule,
  looksTitleCase,
  numberingFindings,
  parseNumberedList,
} from "@/lib/tool-kit/reference-checkers/numbered-list";

export type AmaRule =
  | ListRule
  | "author-periods"
  | "author-comma"
  | "author-natural"
  | "author-and"
  | "et-al-threshold"
  | "et-al-count"
  | "title-quotes"
  | "title-case"
  | "journal-periods"
  | "ieee-locators"
  | "locator-spacing"
  | "locator-date"
  | "page-range"
  | "doi-format"
  | "link-period"
  | "terminal-period";

export type AmaSource = "manual" | "authors" | "journals";

/** Where each rule is written down. The link labels live in the widget's strings. */
export const AMA_SOURCES: Record<AmaSource, string> = {
  manual: "https://www.amamanualofstyle.com/",
  authors: "https://libguides.wvu.edu/ama",
  journals: "https://guides.library.nymc.edu/AMA11/digitalResources",
};

export type AmaFinding = Finding<AmaRule, AmaSource>;
export type AmaReport = CheckReport<AmaRule, AmaSource>;

/** All names up to six; more than six → the first three, then et al. */
const ALL_NAMES_UP_TO = 6;
const NAMES_BEFORE_ET_AL = 3;

function finding(
  rule: AmaRule,
  confidence: "high" | "medium",
  source: AmaSource,
  extra: Pick<AmaFinding, "suggestion" | "values"> = {}
): AmaFinding {
  return { rule, confidence, source, ...extra };
}

// --- authors -----------------------------------------------------------------------

const FORM_RULE = {
  natural: "author-natural",
  comma: "author-comma",
  periods: "author-periods",
} as const;

function etAlFinding(list: AuthorList, fixed: string): AmaFinding | null {
  const count = list.names.length;
  if (count > ALL_NAMES_UP_TO) {
    return finding("et-al-threshold", "high", "authors", {
      suggestion: fixed,
      values: { count },
    });
  }
  if (list.etAl && count !== NAMES_BEFORE_ET_AL) {
    // More than three names before et al. can be cut to three; fewer cannot be filled in.
    return finding("et-al-count", "medium", "authors", {
      suggestion: count > NAMES_BEFORE_ET_AL ? fixed : undefined,
      values: { count },
    });
  }
  return null;
}

function authorFindings(list: AuthorList): AmaFinding[] {
  const findings: AmaFinding[] = [];
  const cut = list.names.length > ALL_NAMES_UP_TO || list.etAl;
  const keep = cut
    ? Math.min(NAMES_BEFORE_ET_AL, list.names.length)
    : list.names.length;
  const fixed = correctedAuthors(list, keep, cut);
  const form = nameFormIssue(list);
  if (form) {
    findings.push(
      finding(FORM_RULE[form], "high", "authors", { suggestion: fixed })
    );
  } else if (usesConjunction(list)) {
    findings.push(
      finding("author-and", "high", "authors", { suggestion: fixed })
    );
  }
  const etAl = etAlFinding(list, fixed);
  if (etAl) {
    findings.push(etAl);
  }
  return findings;
}

// --- title, journal, locator ---------------------------------------------------------

function titleFindings(
  text: string,
  title: TitlePart,
  locator: Locator | null
): AmaFinding[] {
  const findings: AmaFinding[] = [];
  if (title.quoted) {
    findings.push(
      finding("title-quotes", "high", "journals", {
        suggestion: `${title.title}.`,
      })
    );
  }
  if (looksTitleCase(title.title)) {
    findings.push(finding("title-case", "medium", "journals"));
  }
  const journal = locator ? journalSegment(text, title, locator) : null;
  const fixed = journal ? undottedJournal(journal) : null;
  if (journal && fixed) {
    findings.push(
      finding("journal-periods", "high", "journals", {
        suggestion: fixed,
        values: { found: journal, expected: fixed },
      })
    );
  }
  return findings;
}

/** "2019;321(5):456-460" — the year alone before the volume, and full pages. */
function amaLocator(locator: Locator): string {
  let block = `${locator.year};${locator.volume ?? ""}`;
  if (locator.issue !== null) {
    block += `(${locator.issue})`;
  }
  if (locator.first !== null) {
    const pages =
      locator.last === null
        ? locator.first
        : (fullRange(locator.first, locator.last) ??
          `${locator.first}-${locator.last}`);
    block += `:${pages}`;
  }
  return block;
}

function locatorFindings(locator: Locator): AmaFinding[] {
  const findings: AmaFinding[] = [];
  const expected = amaLocator(locator);
  const values = { found: locator.raw.trim(), expected };
  if (locator.month) {
    findings.push(
      finding("locator-date", "medium", "journals", {
        suggestion: expected,
        values,
      })
    );
  } else if (locator.spaced) {
    findings.push(
      finding("locator-spacing", "high", "journals", {
        suggestion: expected,
        values,
      })
    );
  }
  const full =
    locator.first && locator.last
      ? fullRange(locator.first, locator.last)
      : null;
  if (full) {
    findings.push(
      finding("page-range", "high", "journals", {
        suggestion: full,
        values: { found: `${locator.first}-${locator.last}`, expected: full },
      })
    );
  }
  return findings;
}

// --- DOI and the closing period ------------------------------------------------------

const RE_DOI_PREFIX = /\b(doi\s*:\s*)(10\.\d{4,9}\/\S+?)(?=[.,;]?(?:\s|$))/i;
const RE_DOI_URL =
  /\bhttps?:\/\/(?:dx\.)?doi\.org\/(10\.\d{4,9}\/\S+?)(?=[.,;]?(?:\s|$))/i;
const RE_TRAILING_PERIOD = /\.$/;
const RE_LAST_TOKEN = /\S+$/;

const RE_ONLY_PERIOD = /^\.?$/;

interface DoiCheck {
  finding: AmaFinding | null;
  /** The DOI closes the entry; its fix is the finished form, with no period after it. */
  atEnd: boolean;
}

function doiCheck(text: string): DoiCheck {
  const trimmed = text.trim();
  const prefixed = RE_DOI_PREFIX.exec(trimmed);
  if (prefixed?.[1] === "doi:") {
    return { finding: null, atEnd: false };
  }
  const match = prefixed ?? RE_DOI_URL.exec(trimmed);
  if (!match) {
    return { finding: null, atEnd: false };
  }
  const doi = prefixed ? prefixed[2] : match[1];
  const expected = `doi:${doi}`;
  return {
    finding: finding(
      "doi-format",
      "medium",
      prefixed ? "authors" : "journals",
      {
        suggestion: expected,
        values: { found: match[0], expected },
      }
    ),
    atEnd: RE_ONLY_PERIOD.test(trimmed.slice(match.index + match[0].length)),
  };
}

/** AMA ends an entry with a period, except after a closing DOI or URL. */
function endingFinding(text: string): AmaFinding | null {
  const trimmed = text.trim();
  const withoutPeriod = trimmed.replace(RE_TRAILING_PERIOD, "");
  if (withoutPeriod !== trimmed && endsWithLink(withoutPeriod)) {
    // The closing DOI or URL itself, without the period.
    const link = RE_LAST_TOKEN.exec(withoutPeriod)?.[0] ?? withoutPeriod;
    return finding("link-period", "high", "journals", {
      suggestion: link,
      values: { expected: link },
    });
  }
  if (endsWithLink(trimmed) || endsWithTerminal(trimmed)) {
    return null;
  }
  return finding("terminal-period", "high", "journals");
}

// --- the check -----------------------------------------------------------------------

function checkEntry(entry: ListEntry): {
  findings: AmaFinding[];
  surname: string | null;
} {
  const findings: AmaFinding[] = [];
  const authors = parseAuthors(entry.text);
  const locator = findLocator(entry.text);
  if (authors) {
    findings.push(...authorFindings(authors));
    const title = readTitle(entry.text, authors.end);
    if (title) {
      findings.push(...titleFindings(entry.text, title, locator));
    }
  }
  if (locator) {
    findings.push(...locatorFindings(locator));
  } else if (RE_IEEE_LOCATORS.test(entry.text)) {
    findings.push(finding("ieee-locators", "high", "journals"));
  }
  const doi = doiCheck(entry.text);
  const ending = doi.finding && doi.atEnd ? null : endingFinding(entry.text);
  for (const item of [doi.finding, ending]) {
    if (item) {
      findings.push(item);
    }
  }
  return { findings, surname: authors?.names[0]?.surname ?? null };
}

/** Check a pasted AMA reference list. Pure — no network, no mutation. */
export function checkAmaReferences(text: string): AmaReport {
  const list = parseNumberedList(text);
  const surnames: (string | null)[] = [];
  const entries: EntryReport<AmaRule, AmaSource>[] = list.map((entry) => {
    const result = checkEntry(entry);
    surnames.push(result.surname);
    return {
      position: entry.position,
      label: entry.label,
      text: entry.text,
      findings: result.findings,
    };
  });
  const listFindings: AmaFinding[] = [
    ...numberingFindings(list, "manual"),
    ...duplicateFindings(list, "manual"),
  ];
  const alphabetical = alphabeticalFinding(surnames, "manual");
  if (alphabetical) {
    listFindings.push(alphabetical);
  }
  return finishReport(entries, listFindings);
}
